<template>
  <div
    id="files-dashboard"
    :class="{
      fullscreen: isFeatureFullscreen && !mobileScreen,
      mobile: mobileScreen
    }"
  >
    <OverlayCard
      v-if="showConfirmDelete"
      class="overlay-confirm-delete"
      :class="{
        'overlay-confirm-delete__mobile': mobileScreen,
        'overlay-confirm-delete__fullscreen': isFeatureFullscreen && !mobileScreen
      }"
      @closeOverlay="showConfirmDelete = false"
    >
      <ConfirmDeleteAttachmentCard
        :buttonText="t('general__text__delete') + ' ' + t('general__data__file(s)').toLowerCase()"
        @closeOverlay="showConfirmDelete = false"
        @confirmDelete="deleteFiles()"
      />
    </OverlayCard>
    <div class="top-container">
      <div class="search-container">
        <InputSearch
          class="search-container__input"
          :clearInput="clearInput"
          :placeholderText="t('general__text__search-placeholder-files')"
          @emit-search-string="searchStringValue"
        />
      </div>
      <div class="include-child-object-container">
        <!-- Disabled checkbox for realm -->
        <CheckBoxStandard
          class="include-child-object-container__checkbox"
          :checked="includeChildObject"
          :disabled="selectedObject === 0"
          :title="t('general__text__objects-include-child')"
          @valueChanged="includeChildObject = !includeChildObject"
        />
        <InfoLabel
          class="include-child-object-container__info"
          :info="t('tooltip__files-include-child-objects')"
        />
      </div>
    </div>
    <DividerHorizontal v-if="isFeatureFullscreen && !mobileScreen" style="margin: 8px 0 0" />
    <div class="table-content">
      <div class="table-container" :class="{ 'table-container__preview': selectedFile }">
        <PulseLoader
          :loading="isLoadingFirstPage"
          class="spinner"
          :class="{
            spinner__fullscreen: isFeatureFullscreen && !mobileScreen,
            spinner__mobile: mobileScreen
          }"
          color="#1d234f"
        />
        <TableFilesMini
          v-if="!isLoadingFirstPage && (!isFeatureFullscreen || mobileScreen)"
          :fields="fields"
          :items="filesList"
          @archiveFile="archiveFiles"
          @deleteFile="showConfirmDelete = true"
          @downloadFile="downloadFiles"
          @loadMoreFiles="loadMoreFiles"
        />
        <TableFilesLarge
          v-else-if="!isLoadingFirstPage"
          ref="tableFilesLarge"
          class="table-large"
          :fields="fields"
          :items="filesList"
          :resetSorting="resetSorting"
          @archiveFile="archiveFiles"
          @deleteFile="showConfirmDelete = true"
          @downloadFile="downloadFiles"
          @selectedFiles="selectedFiles = $event"
          @sortBy="sortByType"
        />
        <div
          class="bottom-container"
          :class="{ 'bottom-container__no-button': !store.getters.hasFileCreatorAccess }"
        >
          <p
            v-if="isFeatureFullscreen && !mobileScreen && selectedFiles.length >= 2"
            class="selected-files"
          >
            {{ selectedFiles.length + ' ' + t('general__data__files-selected').toLowerCase() }}
          </p>
          <ButtonOutline
            v-if="
              (store.getters.hasFileCreatorAccess && selectedFiles.length <= 1) ||
              (store.getters.hasFileCreatorAccess && !isFeatureFullscreen) ||
              (store.getters.hasFileCreatorAccess && mobileScreen)
            "
            class="bottom-container__button"
            :buttonText="t('general__data__file-upload-a')"
            :iconLeft="iconUploadSmall"
            :size="'small'"
            @onClicked="showModalUploadFiles"
          />
          <div
            v-if="isFeatureFullscreen && !mobileScreen && selectedFiles.length >= 2"
            class="bottom-container__batch-actions"
          >
            <ButtonOutline
              size="small"
              :buttonText="t('general__text__dashboard_reset_selections')"
              @onClicked="resetSelections"
            />
            <TooltipStandardV2
              :text="t('general__text__download')"
              :arrowPosition="'bottom-center'"
              :verticalOffset="2"
            >
              <ButtonOutline
                class="button"
                :iconLeft="iconDownloadSmall"
                :size="'small'"
                @onClicked="downloadFiles"
              />
            </TooltipStandardV2>
            <TooltipStandardV2
              v-if="store.getters.hasFileEditorAccess"
              :text="t('general__text__archive')"
              :arrowPosition="'bottom-center'"
              :verticalOffset="2"
            >
              <ButtonOutline
                class="button"
                :iconLeft="iconArchiveSmall"
                :size="'small'"
                @onClicked="archiveFiles"
              />
            </TooltipStandardV2>
            <TooltipStandardV2
              v-if="store.getters.hasFileAdminAccess"
              :text="t('general__text__delete')"
              :arrowPosition="'bottom-center'"
              :verticalOffset="2"
            >
              <ButtonOutline
                class="button"
                :iconLeft="iconTrash2Small"
                :size="'small'"
                @onClicked="showConfirmDelete = true"
              />
            </TooltipStandardV2>
          </div>
          <PaginationGroupCustom
            v-if="isFeatureFullscreen && !mobileScreen"
            class="bottom-container__pagination"
            :attachmentsPage="filesPage"
            @back="getPreviousPage"
            @next="getNextPage"
            @page="getPage"
          />
        </div>
      </div>
      <div v-if="isFeatureFullscreen && !mobileScreen && selectedFile" class="file-info-container">
        <FileInfo
          @archiveFile="archiveFiles"
          @deleteFile="showConfirmDelete = true"
          @downloadFile="downloadFiles"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import FileInfo from '@/components/features/attachments/FileInfo.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import TableFilesLarge from '@/components/features/attachments/TableFilesLarge.vue'
import TableFilesMini from '@/components/features/attachments/TableFilesMini.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import InputSearch from '@/design-system/molecules/InputSearch.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PaginationGroupCustom from '@/design-system/molecules/PaginationGroupCustom.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import {
  getAttachmentsPage,
  AttachmentsPage,
  archiveAttachments,
  deleteAttachments,
  Attachment,
  Direction
} from '@/services/attachments.service'
import { getAttachmentFileBlob } from '@/services/files.service'
import { debounce } from '@/utils/debounce'
import iconUploadSmall from '@/design-system/assets/icons/Icon-Upload-Small.svg'
import iconDownloadSmall from '@/design-system/assets/icons/Icon-Download-small.svg'
import iconArchiveSmall from '@/design-system/assets/icons/Icon-Archive-Small.svg'
import iconTrash2Small from '@/design-system/assets/icons/Icon-Trash-2-small.svg'

type Emits = {
  (e: 'closeDashboard'): void
}
defineEmits<Emits>()
const { t } = useI18n()
const piniaStore = useGeneralStore()
const store = useStore()

const filesList = computed(() => {
  return piniaStore.filesList
})
const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})
const selectedFile = computed(() => {
  return piniaStore.selectedFile
})
const selectedObject = computed(() => {
  return store.getters.selectedObject
})

const clearInput = ref(false)
const filesPage = ref<AttachmentsPage<Attachment>>()
const includeChildObject = ref(false)
const isLoadingFirstPage = ref(false)
const pageNumber = ref(0)
const resetSorting = ref(false)
const searchString = ref('')
const selectedFiles = ref<Attachment[]>([])
const showConfirmDelete = ref(false)
const sortBy = ref<string>('name')
const sortByOrder = ref<Direction>('asc')
const tableFilesLarge = ref()

const fields = computed(() => {
  const fields =
    !isFeatureFullscreen.value || mobileScreen.value
      ? [
          {
            key: 'name',
            name: filesPage.value
              ? filesPage.value.totalElements + ' ' + t('general__label__files').toLocaleLowerCase()
              : ''
          }
        ]
      : [
          {
            key: 'name',
            name: t('general__text__name'),
            sortable: true
          },
          {
            key: 'actType',
            name: t('general__text__type'),
            sortable: true
          },
          {
            key: 'actStatus',
            name: t('general__text__status'),
            sortable: true
          },
          {
            key: 'createdDate',
            name: t('general__text__uploaded'),
            sortable: true
          },
          {
            key: 'placement',
            name: t('general__text__placement')
          }
        ]
  return fields
})
const filesUploaded = computed(() => {
  return piniaStore.filesUploaded
})
const isSearchActive = computed(() => {
  return searchString.value.length >= 1
})
const mobileScreen = computed(() => {
  return store.state.mobileScreen
})
async function archiveFiles() {
  if (!selectedFile.value && !selectedFiles.value.length) return
  const archiveSingleFile = selectedFile.value || selectedFiles.value.length === 1
  try {
    const ids = selectedFile.value
      ? [selectedFile.value.attachmentId]
      : selectedFiles.value.map((file) => file.attachmentId)
    if (!ids.length) return
    const response = await archiveAttachments(ids)
    if (Array.isArray(response?.data.succeeded) && response?.data.succeeded.length) {
      if (isFeatureFullscreen.value && !mobileScreen.value) {
        // get page to refresh list in fullscreen
        if (filesList.value.length <= response.data.succeeded.length) {
          getPage(0)
        } else {
          getPage(pageNumber.value)
        }
      } else {
        // remove file from filesList in store
        const filesArray = filesList.value.filter(
          (file) =>
            !response.data.succeeded.find(
              (succeededId: string) => succeededId === file.attachmentId
            )
        )
        piniaStore.setFilesList(filesArray)
      }
      // remove selected files from selectedFiles array (in large file dashboard)
      removeFilesFromSelection(response.data.succeeded)
    }
    if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'warning',
        message: `${t('general__data__files-archive-some-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    } else if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      !response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${
          archiveSingleFile
            ? t('general__data__file-archive-failed')
            : t('general__data__files-archive-failed')
        }, ${t('general__text__retry-later-again').toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${
        archiveSingleFile
          ? t('general__data__file-archive-failed')
          : t('general__data__files-archive-failed')
      }, ${t('general__text__retry-later-again').toLowerCase()}`
    })
    console.error(e)
  }
}
async function deleteFiles() {
  if (!selectedFile.value && !selectedFiles.value.length) return
  const deleteSingleFile = selectedFile.value || selectedFiles.value.length === 1
  try {
    const ids = selectedFile.value
      ? [selectedFile.value.attachmentId]
      : selectedFiles.value.map((file) => file.attachmentId)
    if (!ids.length) return
    const response = await deleteAttachments(ids)
    if (Array.isArray(response?.data.succeeded) && response?.data.succeeded.length) {
      if (isFeatureFullscreen.value && !mobileScreen.value) {
        // get page to refresh list in fullscreen
        if (filesList.value.length <= response.data.succeeded.length) {
          getPage(0)
        } else {
          getPage(pageNumber.value)
        }
      } else {
        // remove file from filesList in store
        const filesArray = filesList.value.filter(
          (file) =>
            !response.data.succeeded.find(
              (succeededId: string) => succeededId === file.attachmentId
            )
        )
        piniaStore.setFilesList(filesArray)
      }
      // remove selected files from selectedFiles array (in large file dashboard)
      removeFilesFromSelection(response.data.succeeded)
    }
    if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'warning',
        message: `${t('general__data__files-delete-some-failed')}, ${t(
          'general__text__retry-later-again'
        ).toLowerCase()}`
      })
    } else if (
      !response?.data.success &&
      Object.keys(response?.data.failed) &&
      Array.isArray(response?.data.succeeded) &&
      !response?.data.succeeded.length
    ) {
      store.commit('setInfoMessage', {
        type: 'error',
        message: `${
          deleteSingleFile
            ? t('general__data__file-delete-failed')
            : t('general__data__files-delete-failed')
        }, ${t('general__text__retry-later-again').toLowerCase()}`
      })
    }
  } catch (e) {
    store.commit('setInfoMessage', {
      type: 'error',
      message: `${
        deleteSingleFile
          ? t('general__data__file-delete-failed')
          : t('general__data__files-delete-failed')
      }, ${t('general__text__retry-later-again').toLowerCase()}`
    })
    console.error(e)
  }
}

function downloadFiles() {
  if (!selectedFile.value && !selectedFiles.value.length) return
  const downloadSingleFile = selectedFile.value || selectedFiles.value.length === 1
  async function getFile(file: Attachment) {
    if (!file.storageName || !file.realmId) {
      setDownloadFailedMessage(downloadSingleFile)
      return
    }
    try {
      const res = await getAttachmentFileBlob({
        realmId: file.realmId,
        storageName: file.storageName
      })
      if (!res) return setDownloadFailedMessage(downloadSingleFile)
      const fileUrl = window.URL.createObjectURL(new Blob([res.data]))
      const fileLink = document.createElement('a')
      fileLink.href = fileUrl
      fileLink.setAttribute('download', file.name)
      document.body.appendChild(fileLink)
      fileLink.click()
    } catch (e) {
      setDownloadFailedMessage(downloadSingleFile)
      console.error(e)
    }
  }
  if (selectedFile.value && !selectedFiles.value.length) {
    getFile(selectedFile.value)
  } else {
    for (const file of selectedFiles.value) {
      getFile(file)
    }
  }
}
async function getItems(
  entityId: string,
  page: number,
  fullscreen: boolean,
  sortBy?: string,
  order?: Direction
) {
  const filesArray: Attachment[] = fullscreen ? [] : piniaStore.filesList
  let sortingBy = fullscreen && sortBy !== 'name' ? sortBy : 'nameLower' // nameLower instead of name handles upper case and lower case sorting correctly
  const sortingOrder = fullscreen ? order : 'asc'
  const sort = `${sortingBy},${sortingOrder}`
  filesPage.value = await getAttachmentsPage(
    entityId,
    ['FILE', 'ACT'],
    page,
    fullscreen ? 10 : 25,
    sort,
    null,
    includeChildObject.value
  )
  filesPage.value?.content.forEach((item) => {
    filesArray.push(item)
  })
  piniaStore.setFilesList(filesArray)
}
async function getSearchItems(
  entityId: string,
  searchFilter: string,
  page: number,
  fullscreen: boolean,
  sortBy?: string,
  order?: Direction
) {
  const filesArray: Attachment[] = fullscreen ? [] : piniaStore.filesList
  const sortingBy = fullscreen && sortBy !== 'name' ? sortBy : 'nameLower' // nameLower instead of name handles upper case and lower case sorting correctly
  const sortingOrder = fullscreen ? order : 'asc'
  const sort = `${sortingBy},${sortingOrder}`
  filesPage.value = await getAttachmentsPage(
    entityId,
    ['FILE', 'ACT'],
    page,
    fullscreen ? 10 : 25,
    sort,
    searchFilter,
    includeChildObject.value
  )
  filesPage.value?.content.forEach((item) => {
    filesArray.push(item)
  })
  piniaStore.setFilesList(filesArray)
}
function loadMoreFiles() {
  pageNumber.value++
  if (pageNumber.value === filesPage.value?.totalPages) {
    return
  }
  if (isSearchActive.value) {
    getSearchItems(store.getters.selectedObjectId, searchString.value, pageNumber.value, false)
  } else {
    getItems(store.getters.selectedObjectId, pageNumber.value, false)
  }
}
function getPage(page: number) {
  pageNumber.value = page
  if (isSearchActive.value) {
    getSearchItems(
      store.getters.selectedObjectId,
      searchString.value,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  } else {
    getItems(
      store.getters.selectedObjectId,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  }
}
function getNextPage() {
  pageNumber.value++
  getPage(pageNumber.value)
}
function getPreviousPage() {
  pageNumber.value--
  if (pageNumber.value < 0) {
    pageNumber.value = 0
  }
  getPage(pageNumber.value)
}
function refreshList() {
  resetSorting.value = true
  sortBy.value = 'name'
  sortByOrder.value = 'asc'
  if (isSearchActive.value) {
    resetAndSearchFiles(searchString.value)
  } else {
    resetAndGetFileList()
  }
}
const refreshListDebounce = debounce(() => {
  resetSorting.value = true
  sortBy.value = 'name'
  sortByOrder.value = 'asc'
  if (isSearchActive.value) {
    resetAndSearchFiles(searchString.value)
  } else {
    resetAndGetFileList()
  }
}, 400)
function removeFilesFromSelection(succeededFiles: string[]) {
  const selectedFilesArray = selectedFiles.value.filter(
    (file) => !succeededFiles.find((succeededId: string) => succeededId === file.attachmentId)
  )
  if (selectedFilesArray.length === 1) {
    piniaStore.setSelectedFile(selectedFilesArray[0])
  } else {
    piniaStore.setSelectedFile(null)
  }
  setSelectedFiles(selectedFilesArray)
}
async function resetAndGetFileList() {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setFilesList([])
  pageNumber.value = 0
  isLoadingFirstPage.value = true
  await getItems(
    store.getters.selectedObjectId,
    pageNumber.value,
    isFeatureFullscreen.value,
    sortBy.value,
    sortByOrder.value
  )
  isLoadingFirstPage.value = false
}
async function resetAndSearchFiles(newVal: string) {
  if (!selectedObject.value && selectedObject.value !== 0) return
  if (!Object.keys(selectedObject.value).length && selectedObject.value !== 0) return
  piniaStore.setFilesList([])
  pageNumber.value = 0
  isLoadingFirstPage.value = true
  await getSearchItems(
    store.getters.selectedObjectId,
    newVal,
    0,
    isFeatureFullscreen.value,
    sortBy.value,
    sortByOrder.value
  )
  isLoadingFirstPage.value = false
}
function resetSelections() {
  tableFilesLarge.value?.resetSelections()
}
function searchStringValue(searchValue: string) {
  if (searchValue.length >= 1) clearInput.value = false
  searchString.value = searchValue
}
function setDownloadFailedMessage(downloadSingleFile) {
  store.commit('setInfoMessage', {
    type: 'error',
    message: `${
      downloadSingleFile
        ? t('general__data__file-download-failed')
        : t('general__data__files-download-failed')
    }, ${t('general__text__retry-later-again').toLowerCase()}`
  })
}
function setSelectedFiles(selectedFilesArray: Attachment[] = selectedFiles.value) {
  if (tableFilesLarge.value && isFeatureFullscreen.value) {
    if (selectedFile.value) {
      tableFilesLarge.value.setSelectedFilesFromDashboard([selectedFile.value])
    } else {
      tableFilesLarge.value.setSelectedFilesFromDashboard(selectedFilesArray)
    }
  }
}
function showModalUploadFiles() {
  clearInput.value = true
  piniaStore.setCurrentOverlayModalPinia('uploadFilesPinia')
  piniaStore.setShowOverlayModalPinia(true)
}
function sortByType(key: string, order: Direction) {
  resetSorting.value = false
  sortBy.value = key
  sortByOrder.value = order
  if (!sortBy.value) return
  if (isSearchActive.value) {
    getSearchItems(
      store.getters.selectedObjectId,
      searchString.value,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  } else {
    getItems(
      store.getters.selectedObjectId,
      pageNumber.value,
      true,
      sortBy.value,
      sortByOrder.value
    )
  }
}
watch(filesList, (newList, oldList) => {
  // remove one item from list when a file deleted
  if (!filesPage.value?.totalElements) return
  // Todo: The line below makes the totalElements value incorrect for the last page
  if (newList.length < oldList.length) filesPage.value.totalElements--
})
watch(
  filesUploaded,
  (newFilesArray, oldFilesArray) => {
    if (newFilesArray.length && newFilesArray.length > oldFilesArray.length) {
      piniaStore.setFilesList([])
      pageNumber.value = 0
      getItems(
        store.getters.selectedObjectId,
        pageNumber.value,
        isFeatureFullscreen.value,
        sortBy.value,
        sortByOrder.value
      )
    }
  },
  { deep: true }
)
watch(includeChildObject, (newVal, oldVal) => {
  if (newVal === false && oldVal === true) {
    piniaStore.setSelectedFile(null)
    setSelectedFiles([])
  }
  refreshList()
})
watch(searchString, (newVal, oldVal) => {
  if (newVal !== oldVal) refreshListDebounce()
})
watch(selectedFile, (newFile) => {
  if (!isFeatureFullscreen.value || mobileScreen.value) {
    if (!newFile) selectedFiles.value = []
  }
})
watch(
  selectedObject,
  async () => {
    // Don't include child objects for realm
    if (selectedObject.value === 0) includeChildObject.value = false
    refreshList()
    piniaStore.setSelectedFile(null)
  },
  { immediate: true }
)
watch(isFeatureFullscreen, () => {
  refreshList()
})
watch(tableFilesLarge, () => {
  setSelectedFiles()
})
onUnmounted(() => {
  piniaStore.setSelectedFile(null)
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.overlay-confirm-delete {
  left: -72px;
  top: -16px;
  &__mobile,
  &__fullscreen {
    left: 0;
    top: 0;
  }
}
.spinner {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding-top: 80px;
  &__fullscreen {
    padding-bottom: 15%;
  }
  &__mobile {
    padding-bottom: 50%;
  }
}
#files-dashboard {
  background-color: #fff;
  border-bottom-right-radius: variables.$border-radius-small;
  border-bottom-left-radius: variables.$border-radius-small;
  display: flex;
  flex-flow: column;
  height: fit-content;
  max-height: calc(
    100vh - #{variables.$layout-top-bar-height} - #{variables.$padding-xlarge} - 152px
  );
  min-height: 400px;
  overflow: hidden;
  width: 100%;
  .top-container {
    display: flex;
    flex-direction: column;
    margin-top: 8px;
    width: 100%;
    .search-container {
      display: flex;
      height: 40px;
      margin: 8px 0;
      &__input {
        padding: 0 24px;
        width: 100%;
        :deep(.text-field-standard) {
          height: 40px;
        }
      }
    }
    .include-child-object-container {
      align-items: top;
      display: flex;
      height: 40px;
      padding: 0 24px;
      &__checkbox {
        margin-left: -3px;
        margin-right: 5px;
        padding: 0;
      }
      &__checkbox-placeholder {
        border-radius: 4px;
        border: 1px solid #767676;
        cursor: not-allowed;
        height: 18px;
        margin-right: 8px;
        margin-top: 3px;
        width: 18px;
      }
      &__info {
        height: 24px;
      }
      &__text {
        color: variables.$color-text-secondary;
        padding-right: 4px;
        @include font-mixins.desktop-regular-paragraph;
      }
    }
  }
  .table-content {
    display: contents;
    .table-container {
      display: contents;
    }
  }
  .bottom-container {
    background-color: #fff;
    border-top: 1px solid variables.$color-greyscale-line-grey;
    display: flex;
    flex: 0 0 72px;
    flex-flow: column;
    height: 72px;
    justify-self: flex-end;
    margin-top: auto;
    padding: 16px variables.$padding-main-body;
    &__button {
      width: 100%;
    }
    &__no-button {
      border-top: none;
      flex: 0 0 24px;
      height: 24px;
      padding: 0;
    }
  }
  .file-info-container {
    display: none;
  }
  &.fullscreen {
    border-radius: 0;
    height: 100%;
    max-height: 100%;
    .top-container {
      align-items: center;
      flex-direction: row;
      gap: 16px;
      .search-container {
        &__input {
          margin-left: 24px;
          padding: 0;
          width: 310px;
        }
      }
      .include-child-object-container {
        align-items: center;
        padding: 0;
      }
    }
    .table-content {
      display: flex;
      flex-direction: row;
      height: 100%;
      overflow: hidden;
      .table-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        justify-content: space-between;
        overflow: hidden;
        padding: 16px variables.$padding-main-body 6px;
        width: 100%;
        &__preview {
          padding: 16px 16px 6px variables.$padding-main-body;
        }
        .table-large {
          height: auto;
        }
        .bottom-container {
          align-items: center;
          border-top: none;
          flex-flow: row;
          gap: 16px;
          height: fit-content;
          justify-content: flex-start;
          padding: 16px 0 0;
          &__batch-actions {
            align-items: center;
            display: flex;
            gap: 16px;
            height: 32px;
            justify-content: flex-end;
            .button {
              width: 40px;
            }
          }
          &__button {
            width: fit-content;
          }
          &__pagination {
            margin-left: auto;
          }
          .selected-files {
            color: #000;
            @include font-mixins.desktop-bold-label;
          }
        }
      }
      .file-info-container {
        border-left: 1px solid variables.$color-greyscale-line-grey;
        display: flex;
        max-width: 415px;
        min-width: 415px;
        padding: 16px 24px 24px 16px;
        width: 415px;
      }
    }
  }
  &.mobile {
    border-radius: 0;
    bottom: 0;
    height: 100%;
    left: 0;
    position: fixed;
    right: 0;
    top: calc(#{variables.$layout-top-bar-height} + 56px);
    max-height: calc(100vh - #{variables.$layout-top-bar-height} - 56px);
    width: 100%;
  }
}
</style>
